import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, Shield, Lock, Smartphone, Eye, EyeOff, Key, AlertTriangle, CheckCircle, Loader2 } from 'lucide-react';
import { DriverBottomNav } from '../components/DriverBottomNav';
import { settingsService } from '../services/settingsService';
import { userService } from '../services/userService';

export function DriverMobileSecurity() {
  const navigate = useNavigate();
  
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  const [autoLogoutEnabled, setAutoLogoutEnabled] = useState(true);
  const [autoLogoutMinutes, setAutoLogoutMinutes] = useState(15);
  
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [driverUserId, setDriverUserId] = useState<string | null>(null);

  useEffect(() => {
    const loadSecuritySettings = async () => {
      const userData = sessionStorage.getItem('driverMobileUser');

      if (!userData) {
        setLoading(false);
        return;
      }

      try {
        const parsedUser = JSON.parse(userData);
        const userId = String(parsedUser.id);
        setDriverUserId(userId);

        const savedSettings = await settingsService.getMySettingValue(`user_${userId}_driver_security_settings`, {
          twoFactorEnabled: false,
          biometricEnabled: false,
          autoLogoutEnabled: true,
          autoLogoutMinutes: 15,
        });

        setTwoFactorEnabled(savedSettings.twoFactorEnabled || false);
        setBiometricEnabled(savedSettings.biometricEnabled || false);
        setAutoLogoutEnabled(savedSettings.autoLogoutEnabled !== false);
        setAutoLogoutMinutes(savedSettings.autoLogoutMinutes || 15);
      } catch (error) {
        console.error('Error loading security settings:', error);
        setErrorMessage('Guvenlik ayarlari yuklenemedi');
        setShowError(true);
      } finally {
        setLoading(false);
      }
    };

    loadSecuritySettings();
  }, []);

  const validatePassword = (password: string): boolean => {
    if (password.length < 8) {
      setErrorMessage('Şifre en az 8 karakter olmalıdır');
      return false;
    }
    if (!/[A-Z]/.test(password)) {
      setErrorMessage('Şifre en az bir büyük harf içermelidir');
      return false;
    }
    if (!/[a-z]/.test(password)) {
      setErrorMessage('Şifre en az bir küçük harf içermelidir');
      return false;
    }
    if (!/[0-9]/.test(password)) {
      setErrorMessage('Şifre en az bir rakam içermelidir');
      return false;
    }
    return true;
  };

  const handleChangePassword = async () => {
    setShowError(false);
    setShowSuccess(false);

    if (!currentPassword) {
      setErrorMessage('Mevcut şifrenizi giriniz');
      setShowError(true);
      return;
    }

    if (!newPassword || !confirmPassword) {
      setErrorMessage('Yeni şifre alanlarını doldurunuz');
      setShowError(true);
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMessage('Yeni şifreler eşleşmiyor');
      setShowError(true);
      return;
    }

    if (!validatePassword(newPassword)) {
      setShowError(true);
      return;
    }

    // Get current user
    const userData = sessionStorage.getItem('driverMobileUser');
    if (!userData) {
      setErrorMessage('Oturum bulunamadı');
      setShowError(true);
      return;
    }

    const user = JSON.parse(userData);

    try {
      setSaving(true);
      await userService.changePassword(user.id, {
        currentPassword,
        newPassword,
      });
    } catch (error: any) {
      setErrorMessage(error?.message || 'Sifre degistirilirken hata olustu');
      setShowError(true);
      setSaving(false);
      return;
    }

    // Clear form
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
    setSaving(false);
  };

  const handleSaveSettings = () => {
    if (!driverUserId) {
      setErrorMessage('Oturum bulunamadi');
      setShowError(true);
      return;
    }

    const saveSettings = async () => {
      try {
        setSaving(true);
        setShowError(false);
        await settingsService.saveMySetting(`user_${driverUserId}_driver_security_settings`, {
          twoFactorEnabled,
          biometricEnabled,
          autoLogoutEnabled,
          autoLogoutMinutes,
        }, {
          category: 'driver-security',
          description: 'Surucu mobil guvenlik ayarlari',
          updatedBy: driverUserId,
        });

        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
      } catch (error) {
        console.error('Failed to save security settings:', error);
        setErrorMessage('Guvenlik ayarlari kaydedilemedi');
        setShowError(true);
      } finally {
        setSaving(false);
      }
    };

    void saveSettings();
  };

  const handleBiometricToggle = async () => {
    if (!biometricEnabled) {
      // Check if biometric is available
      if ('credentials' in navigator && 'PublicKeyCredential' in window) {
        // WebAuthn is available
        setBiometricEnabled(true);
      } else {
        setErrorMessage('Biyometrik doğrulama bu cihazda desteklenmiyor');
        setShowError(true);
        setTimeout(() => setShowError(false), 3000);
      }
    } else {
      setBiometricEnabled(false);
    }
  };

  const getPasswordStrength = (password: string): { strength: number; label: string; color: string } => {
    let strength = 0;
    
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;

    if (strength <= 2) return { strength: 1, label: 'Zayıf', color: 'bg-red-500' };
    if (strength <= 4) return { strength: 2, label: 'Orta', color: 'bg-yellow-500' };
    return { strength: 3, label: 'Güçlü', color: 'bg-green-500' };
  };

  const passwordStrength = newPassword ? getPasswordStrength(newPassword) : null;

  return (
    <div className="safe-area-page min-h-dvh bg-gray-50 dark:bg-[#0b0b0b]">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white p-4 pb-6 shadow-sm dark:border-slate-800 dark:bg-gradient-to-r dark:from-blue-900 dark:to-blue-800 dark:text-white dark:shadow-lg">
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-100 px-3 py-2 text-slate-900 ring-1 ring-slate-200 transition-colors hover:bg-slate-200 dark:bg-white/15 dark:text-white dark:ring-white/20 dark:hover:bg-white/20"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Geri</span>
          </button>
          <h1 className="text-lg font-bold text-slate-900 dark:text-white">Güvenlik Ayarları</h1>
        </div>

        <p className="text-sm text-slate-500 dark:text-blue-200">
          Hesabınızı güvende tutun
        </p>
      </div>

      {/* Success Message */}
      {showSuccess && (
        <div className="mx-4 mt-4 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <p className="text-sm text-green-700 font-medium">Ayarlar başarıyla kaydedildi!</p>
        </div>
      )}

      {/* Error Message */}
      {showError && (
        <div className="mx-4 mt-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <p className="text-sm text-red-700 font-medium">{errorMessage}</p>
        </div>
      )}

      <div className="px-4 pt-6 space-y-4">
        {loading && (
          <div className="bg-white rounded-xl shadow-lg p-4 flex items-center justify-center gap-3 text-gray-600">
            <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
            <span>Ayarlar yukleniyor...</span>
          </div>
        )}

        {/* Change Password */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="p-4 border-b bg-gray-50">
            <div className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-blue-600" />
              <h2 className="font-bold text-gray-900">Şifre Değiştir</h2>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              Güvenli bir şifre oluşturun
            </p>
          </div>
          <div className="p-4 space-y-4">
            {/* Current Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mevcut Şifre
              </label>
              <div className="relative">
                <input
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Mevcut şifrenizi giriniz"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Yeni Şifre
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Yeni şifrenizi giriniz"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              
              {/* Password Strength Indicator */}
              {newPassword && passwordStrength && (
                <div className="mt-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-600">Şifre Gücü:</span>
                    <span className={`text-xs font-medium ${
                      passwordStrength.strength === 1 ? 'text-red-600' :
                      passwordStrength.strength === 2 ? 'text-yellow-600' : 'text-green-600'
                    }`}>
                      {passwordStrength.label}
                    </span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all ${passwordStrength.color}`}
                      style={{ width: `${(passwordStrength.strength / 3) * 100}%` }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Yeni Şifre (Tekrar)
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Yeni şifrenizi tekrar giriniz"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {confirmPassword && newPassword !== confirmPassword && (
                <p className="text-xs text-red-600 mt-1">Şifreler eşleşmiyor</p>
              )}
            </div>

            {/* Password Requirements */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs font-medium text-blue-900 mb-2">Şifre Gereksinimleri:</p>
              <ul className="text-xs text-blue-700 space-y-1">
                <li className="flex items-center gap-2">
                  <div className={`w-1 h-1 rounded-full ${newPassword.length >= 8 ? 'bg-green-500' : 'bg-gray-400'}`} />
                  En az 8 karakter
                </li>
                <li className="flex items-center gap-2">
                  <div className={`w-1 h-1 rounded-full ${/[A-Z]/.test(newPassword) ? 'bg-green-500' : 'bg-gray-400'}`} />
                  En az bir büyük harf
                </li>
                <li className="flex items-center gap-2">
                  <div className={`w-1 h-1 rounded-full ${/[a-z]/.test(newPassword) ? 'bg-green-500' : 'bg-gray-400'}`} />
                  En az bir küçük harf
                </li>
                <li className="flex items-center gap-2">
                  <div className={`w-1 h-1 rounded-full ${/[0-9]/.test(newPassword) ? 'bg-green-500' : 'bg-gray-400'}`} />
                  En az bir rakam
                </li>
              </ul>
            </div>

            <button
              onClick={handleChangePassword}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 rounded-xl font-bold shadow-lg hover:shadow-xl transition-shadow"
            >
              Şifreyi Değiştir
            </button>
          </div>
        </div>

        {/* Security Options */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="p-4 border-b bg-gray-50">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-green-600" />
              <h2 className="font-bold text-gray-900">Güvenlik Seçenekleri</h2>
            </div>
          </div>
          <div>
            {/* Two-Factor Authentication */}
            <div className="p-4 flex items-center justify-between border-b">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <Key className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">İki Faktörlü Doğrulama</p>
                  <p className="text-sm text-gray-500">SMS ile ek güvenlik katmanı</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={twoFactorEnabled}
                  onChange={() => setTwoFactorEnabled(!twoFactorEnabled)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
              </label>
            </div>

            {/* Biometric Login */}
            <div className="p-4 flex items-center justify-between border-b">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
                  <Smartphone className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Biyometrik Giriş</p>
                  <p className="text-sm text-gray-500">Parmak izi veya yüz tanıma</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={biometricEnabled}
                  onChange={handleBiometricToggle}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
              </label>
            </div>

            {/* Auto Logout */}
            <div className="p-4 border-b">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0">
                    <Lock className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Otomatik Çıkış</p>
                    <p className="text-sm text-gray-500">Hareketsizlikte otomatik çıkış yap</p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={autoLogoutEnabled}
                    onChange={() => setAutoLogoutEnabled(!autoLogoutEnabled)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                </label>
              </div>
              
              {autoLogoutEnabled && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Çıkış Süresi (dakika)
                  </label>
                  <select
                    value={autoLogoutMinutes}
                    onChange={(e) => setAutoLogoutMinutes(Number(e.target.value))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  >
                    <option value={5}>5 dakika</option>
                    <option value={10}>10 dakika</option>
                    <option value={15}>15 dakika</option>
                    <option value={30}>30 dakika</option>
                    <option value={60}>60 dakika</option>
                  </select>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Save Button */}
        <button
          onClick={handleSaveSettings}
          disabled={saving || loading}
          className="w-full bg-gradient-to-r from-green-600 to-green-700 text-white py-4 rounded-xl font-bold shadow-lg hover:shadow-xl transition-shadow"
        >
          {saving ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
        </button>
      </div>

      {/* Bottom Navigation */}
      <DriverBottomNav />
    </div>
  );
}
